import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FM110, FM111, m201Api } from '../../services/m201Api';
import { auditLogApi } from '../../services/auditLogApi';
import { useM201Mode } from './hooks/useM201Mode';
import { useM201Record } from './hooks/useM201Record';
import { useM201Navigation } from './hooks/useM201Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import { SharedActionBar } from '../../components/SharedActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import {
  AcceptanceDataPanel,
  AcceptanceToolbar,
  pwm201f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';
import './M201Form.css';

const M201Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRow, setSelectedRow] = useState(0);
  const [acceptanceLatest, setAcceptanceLatest] = useState<FM110[]>([]);
  const [acceptanceHistory, setAcceptanceHistory] = useState<unknown[]>([]);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<FM110 | null>(null);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const acceptanceMode = useAcceptanceMode();
  const acceptanceEnabled = acceptanceMode.isEnabled(pwm201f1AcceptanceManifest.formId);

  // Hooks
  const perm = useModulePermission('pwm201f1');
  const { mode, setMode, isEditing } = useM201Mode();
  const {
    record, lastApiResponse, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useM201Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useM201Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecord(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load M201 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing && mode === 'add') {
      await handleSave(mode, async (result: FM110) => {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fm110_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setSelectedRow(0);
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(async () => {
    if (isEditing && mode === 'edit') {
      await handleSave(mode, async (result: FM110) => {
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('edit');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除異動單 ${record?.fm110_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleExit = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      navigate('/erp');
    }
  }, [isEditing, revertToRecord, setMode, navigate]);

  // Save (for SharedActionBar onSave)
  const handleSaveAction = useCallback(async () => {
    if (mode === 'add') {
      await handleAddAction();
    } else if (mode === 'edit') {
      await handleModifyAction();
    }
  }, [mode, handleAddAction, handleModifyAction]);

  // Cancel (for SharedActionBar onCancel)
  const handleCancelAction = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  // ── Approval Handlers ──
  const handleSubmit = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定送審此異動單「${record.fm110_01}」？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          await m201Api.submitForApproval(record.fm110_01);
          message.success('已送審');
          await loadRecord(record.fm110_01);
        } catch {
          message.error('送審失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定核准此異動單「${record.fm110_01}」？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          await m201Api.approve(record.fm110_01);
          message.success('已核准');
          await loadRecord(record.fm110_01);
        } catch {
          message.error('核准失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleReject = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定退回此異動單「${record.fm110_01}」？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await m201Api.reject(record.fm110_01);
          message.success('已退回');
          await loadRecord(record.fm110_01);
        } catch {
          message.error('退回失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleWriteoff = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fm110_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await m201Api.writeoff(record.fm110_01);
          message.success('沖銷成功');
          await loadRecord(record.fm110_01);
        } catch {
          message.error('沖銷失敗');
        }
      },
    });
  }, [record, loadRecord]);

  /** Handle detail field changes */
  const handleDetailChange = useCallback((idx: number, key: keyof FM111, value: FM111[keyof FM111]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  // Status code for approval
  const statusCode = (record?.fm110_80 || '').trim();

  const loadAcceptanceData = useCallback(async () => {
    if (!acceptanceEnabled) return;
    setAcceptanceLoading(true);
    const [latest, history, database, audit] = await Promise.allSettled([
      m201Api.list(1, 3),
      record?.fm110_01 ? m201Api.getApprovalHistory(record.fm110_01) : Promise.resolve([]),
      record?.fm110_01 ? m201Api.get(record.fm110_01) : Promise.resolve(null),
      auditLogApi.list({ form_id: pwm201f1AcceptanceManifest.formId, doc_key: record?.fm110_01, page: 1, pageSize: 20 }),
    ]);
    setAcceptanceLatest(latest.status === 'fulfilled' ? latest.value.data || [] : []);
    setAcceptanceHistory(history.status === 'fulfilled' ? history.value : []);
    setAcceptanceDatabaseRecord(database.status === 'fulfilled' ? database.value : null);
    setAcceptanceAudit(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setAcceptanceErrors([
      ...(latest.status === 'rejected' ? ['最新三筆 API 讀取失敗'] : []),
      ...(history.status === 'rejected' ? ['審核歷程 API 讀取失敗'] : []),
      ...(database.status === 'rejected' ? ['DB 即時單據 API 讀取失敗'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗或目前使用者無權限'] : []),
    ]);
    setAcceptanceLoading(false);
  }, [acceptanceEnabled, record?.fm110_01]);

  useEffect(() => {
    void loadAcceptanceData();
  }, [loadAcceptanceData]);

  const handleAcceptanceAutoFill = useCallback(() => {
    if (!record) {
      message.warning('請先開啟一筆已存在且可用的單據，驗收模式會用它建立不含單號的測試草稿。');
      return;
    }
    const sampleMaster = { ...record };
    const sampleDetails = (record.details || details).map((detail, index) => ({
      ...detail,
      fm111_01: '',
      fm111_02: index + 1,
      fm111_90: 0,
      fm111_91: '',
      fm111_92: null,
      fm111_93: '',
    }));
    setMode('add');
    resetForAdd();
    setField('fm110_11', new Date().toISOString().slice(0, 10));
    setField('fm110_13', sampleMaster.fm110_13);
    setField('fm110_16', sampleMaster.fm110_16);
    setField('fm110_17', sampleMaster.fm110_17);
    setField('fm110_18', sampleMaster.fm110_18 || '+');
    setField('fm110_22', sampleMaster.fm110_22 || '');
    setDetails(sampleDetails);
    setSelectedRow(sampleDetails.length ? 0 : -1);
    message.success('已帶入測試草稿；按儲存時仍會走原本 M201 API 與全部驗證。');
  }, [details, record, resetForAdd, setDetails, setField, setMode]);

  return (
    <div className="m201-form">
      <div className="page-wrapper">
        {/* Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a>
              <span>/</span>
              <span className="current">產品異動調整單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>產品異動調整單 (pwm201f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fm110_01 || '(新增)'}
            </span>
          </div>
        </div>

      <AcceptanceToolbar
        manifest={pwm201f1AcceptanceManifest}
        onAutoFill={handleAcceptanceAutoFill}
        documentKey={record?.fm110_01}
          autoFillDisabled={!record || isEditing}
          autoFillReason={!record ? '請先開啟一筆可作為範例的既有單據' : isEditing ? '請先儲存或取消目前編輯' : '複製目前單據的業務欄位，建立新的驗收草稿'}
        />

        {/* Form Info Bar */}
        <div className="form-info-bar">
          <div className="field-group">
            <label>異動單號</label>
            <strong>{formData.fm110_01 || '--'}</strong>
          </div>
          <div className="field-group">
            <label data-contract-field="ui_mode">模式</label>
            <span className={`badge ${isEditing ? 'badge-warning' : 'badge-default'}`}>
              {mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}
            </span>
          </div>
        </div>

        {/* Master Fields + Detail Grid (single screen, no tabs) */}
        <div className="form-content">
          <Tab1BasicInfo
            formData={formData}
            isEditing={isEditing}
            getField={getField}
            setField={setField}
            documentKey={record?.fm110_01}
          />
          <Tab2Details
            details={details}
            isEditing={isEditing}
            onAdd={addDetailRow}
            onDelete={deleteDetailRow}
            onDetailChange={handleDetailChange}
            selectedRow={selectedRow}
            onSelectRow={setSelectedRow}
            totalCount={Number(getField('fm110_14')) || details.length}
            documentKey={record?.fm110_01}
          />
        </div>

        {/* Action Bar */}
        <SharedActionBar
          acceptanceActionIds={{
            list: 'PWM201-ACT-QUERY',
            create: 'PWM201-ACT-CREATE',
            update: 'PWM201-ACT-UPDATE',
            delete: 'PWM201-ACT-DELETE',
          }}
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAddAction}
          onModify={() => { if (!record) return; setMode('edit'); }}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancelAction}
          approval={{
            statusCode,
            isApproved: statusCode === 'P',
            isPending: ['D', 'S'].includes(statusCode), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes(statusCode),
          }}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onWriteoff={handleWriteoff}
          onHistory={() => setShowHistory(true)}
          onExit={handleExit}
          canCreate={perm.canCreate}
          canEdit={perm.canEdit}
          canDelete={perm.canDelete}
          canApprove={perm.canApprove}
        />

        {/* Approval History Modal */}
        <ApprovalHistoryModal
          visible={showHistory}
          orderNo={record?.fm110_01 || ''}
          onClose={() => setShowHistory(false)}
        />

        <AcceptanceDataPanel
          manifest={pwm201f1AcceptanceManifest}
          documentKey={record?.fm110_01}
          snapshots={{
            screenDraft: { master: formData, details },
            lastApiResponse: lastApiResponse ? { master: lastApiResponse, details: lastApiResponse.details || [] } : null,
            databaseRecord: acceptanceDatabaseRecord ? { master: acceptanceDatabaseRecord, details: acceptanceDatabaseRecord.details || [] } : null,
          }}
          latestRecords={acceptanceLatest}
          history={acceptanceHistory}
          auditEntries={acceptanceAudit}
          loading={acceptanceLoading || loading}
          onRefresh={() => { void loadAcceptanceData(); }}
          observationErrors={acceptanceErrors}
        />
      </div>
    </div>
  );
};

export default M201Form;
